/**
 * Profile: download my data / delete my account (audit P-4, 2026-10-21).
 *
 * Deletion anonymizes the account (owner decision): identity, sign-in and
 * chat history are removed; submitted reports stay without your identity.
 * The user types DELETE to confirm. The Backend refuses while a subscription
 * is live or while the user is their department's only admin; its message
 * is shown as-is.
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { deleteGuardianAccount, exportGuardianData } from '../api/guardianApi';

export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function AccountDataSection({ authToken, onDeleted }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const onExport = async () => {
    setExporting(true);
    setExportError(null);
    const res = await exportGuardianData(authToken);
    setExporting(false);
    if (res?.success && res.data) {
      downloadJson(res.data, 'guardian-data-export.json');
    } else {
      setExportError(res?.status === 429
        ? 'You can download your data 3 times an hour. Try again later.'
        : (res?.message || 'Could not prepare your data. Try again.'));
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    const res = await deleteGuardianAccount(authToken);
    setDeleting(false);
    if (res?.success) {
      onDeleted?.();
      return;
    }
    setDeleteError(res?.message || 'Could not delete your account. Try again.');
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Your data</Typography>
      <Box>
        <Button variant="outlined" onClick={onExport} disabled={exporting}>
          {exporting ? 'Preparing…' : 'Download my data'}
        </Button>
      </Box>
      {exportError && <Alert severity="error">{exportError}</Alert>}

      {!confirming ? (
        <Box>
          <Button color="error" onClick={() => setConfirming(true)}>Delete my account</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Alert severity="warning">
            This permanently removes your sign-in, profile and chat history. Incident reports you
            submitted stay, without your identity. Type DELETE to confirm.
          </Alert>
          <TextField
            size="small"
            label="Type DELETE"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputProps={{ 'aria-label': 'Type DELETE to confirm' }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => { setConfirming(false); setTyped(''); }}>Cancel</Button>
            <Button color="error" variant="contained" disabled={typed !== 'DELETE' || deleting} onClick={onDelete}>
              {deleting ? 'Deleting…' : 'Delete my account'}
            </Button>
          </Box>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </Box>
      )}
    </Paper>
  );
}
